import { Box, Checkbox, FormControlLabel, FormHelperText } from '@mui/material';
import type { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { hasClickableFieldHelp, hasFieldHelpIcon, VNFieldHelpChrome, VNFieldHelpIconButton } from '@/components/VNFieldHelp';
import { useVNFieldHelpController } from '@/components/VNFieldHelp/useVNFieldHelpController';
import VNInfoIcon from '@/components/VNInfoIcon/VNInfoIcon';

type BaseProps = {
  name: string;
  label: ReactNode;
  disabled?: boolean;
  helperText?: React.ReactNode;
};

type Props = Omit<VNFieldHelpProps, 'helperText'> &
  BaseProps & {
    /** @deprecated Use `helperTooltip` instead. */
    infoText?: ReactNode;
  };

const VNCheckBoxBody = ({ name, disabled, helperText, labelNode }: BaseProps & { labelNode: ReactNode }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <>
          <FormControlLabel
            control={
              <Checkbox {...field} checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={disabled} />
            }
            label={labelNode}
          />
          {helperText && (
            <FormHelperText style={{ marginLeft: 38 }} component="div">
              {helperText}
            </FormHelperText>
          )}
        </>
      )}
    />
  );
};

const VNCheckBoxWithHelp = ({ name, label, disabled, helperText, ...fieldHelpProps }: Omit<Props, 'infoText'>) => {
  const {
    containerRef,
    panelOpen,
    drawerOpen,
    isActive,
    handleIconClick,
    drawerAriaLabel,
    closeDrawer,
    helperTooltip,
    helperText: fieldHelpText,
    helperContent,
    helperLink,
    helperContentTitle,
  } = useVNFieldHelpController(fieldHelpProps);

  const labelNode = (
    <span className="vn-field-help__label vn-field-help__label--inline">
      <span>{label}</span>
      <VNFieldHelpIconButton
        active={isActive}
        helperTooltip={helperTooltip}
        onClick={hasClickableFieldHelp(fieldHelpProps) ? handleIconClick : undefined}
        ariaLabel={drawerAriaLabel}
      />
    </span>
  );

  const rootClass = ['vn-checkbox', 'vn-field-help', panelOpen && 'vn-field-help--help-open'].filter(Boolean).join(' ');

  return (
    <div className={rootClass} ref={containerRef}>
      <VNCheckBoxBody name={name} disabled={disabled} helperText={helperText} label={label} labelNode={labelNode} />
      <VNFieldHelpChrome
        panelOpen={panelOpen}
        drawerOpen={drawerOpen}
        helperText={fieldHelpText}
        helperLink={helperLink}
        helperContent={helperContent}
        helperContentTitle={helperContentTitle}
        onCloseDrawer={closeDrawer}
      />
    </div>
  );
};

const VNCheckBoxLegacyInfo = ({ name, label, disabled, helperText, infoText }: Props) => {
  const labelNode =
    infoText != null && infoText !== '' ? (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        <Box component="span" sx={{ alignSelf: 'flex-start', lineHeight: 0, display: 'inline-flex' }}>
          <VNInfoIcon description={infoText} />
        </Box>
      </Box>
    ) : (
      label
    );

  return (
    <div className="vn-checkbox">
      <VNCheckBoxBody name={name} disabled={disabled} helperText={helperText} label={label} labelNode={labelNode} />
    </div>
  );
};

const VNCheckBox = ({ name, label, disabled, helperText, infoText, helperTooltip, infoTooltip, ...helpRest }: Props) => {
  const resolvedFieldHelp: VNFieldHelpProps = {
    helperTooltip: helperTooltip ?? infoTooltip ?? (typeof infoText === 'string' ? infoText : undefined),
    ...helpRest,
  };

  if (hasFieldHelpIcon(resolvedFieldHelp)) {
    return <VNCheckBoxWithHelp name={name} label={label} disabled={disabled} helperText={helperText} {...resolvedFieldHelp} />;
  }

  return <VNCheckBoxLegacyInfo name={name} label={label} disabled={disabled} helperText={helperText} infoText={infoText} />;
};

export default VNCheckBox;
